import { type ReactNode, useCallback } from "react";
import { createPortal } from "react-dom";
import { usePostHog } from "posthog-js/react";
import { useWallet } from "@solana/wallet-adapter-react";
import type { Wallet } from "@solana/wallet-adapter-react";
import BottomSheet from "@/components/ui/BottomSheet";
import Icon from "@/components/ui/Icon";

interface DeeplinkWallet {
    name: string;
    icon: string;
    buildBrowseUrl: (targetUrl: string) => string;
}

const DEEPLINK_WALLETS: DeeplinkWallet[] = [
    {
        name: "Phantom",
        icon: "/icons/phantom.ico",
        buildBrowseUrl: (targetUrl: string) =>
            `https://phantom.app/ul/browse/${encodeURIComponent(targetUrl)}?ref=${encodeURIComponent(window.location.origin)}`,
    },
    {
        name: "Solflare",
        icon: "/icons/solflare.svg",
        buildBrowseUrl: (targetUrl: string) =>
            `https://solflare.com/ul/v1/browse/${encodeURIComponent(targetUrl)}?ref=${encodeURIComponent(window.location.origin)}`,
    },
];

interface MobileWalletSheetProps {
    open: boolean;
    onClose: () => void;
}

export default function MobileWalletSheet({
    open,
    onClose,
}: MobileWalletSheetProps): ReactNode {
    const posthog = usePostHog();
    const { wallets, select, connect } = useWallet();

    const handleDeeplink = (wallet: DeeplinkWallet) => {
        posthog.capture("mobile_wallet_selected", { wallet: wallet.name, method: "deeplink" });
        window.location.href = wallet.buildBrowseUrl(window.location.href);
    };

    const handleBrowserWallet = useCallback(async (wallet: Wallet) => {
        posthog.capture("mobile_wallet_selected", { wallet: wallet.adapter.name, method: "browser" });
        select(wallet.adapter.name);
        setTimeout(() => {
            connect().catch(() => {});
            onClose();
        }, 100);
    }, [select, connect, onClose, posthog]);

    // Filter out deeplink wallets that match a detected browser wallet (avoid duplicates)
    const deeplinkNames = new Set(DEEPLINK_WALLETS.map((w) => w.name.toLowerCase()));
    const browserWallets = wallets.filter(
        (w) => !deeplinkNames.has(w.adapter.name.toLowerCase())
    );

    return createPortal(
        <BottomSheet open={open} onClose={onClose} title="Connect Wallet">
            {/* Deep link wallets (open in wallet's in-app browser) */}
            <p className="text-white/40 text-xs mb-3 leading-relaxed">
                Open in wallet app
            </p>
            <div className="space-y-2">
                {DEEPLINK_WALLETS.map((wallet) => (
                    <button
                        key={wallet.name}
                        onClick={() => handleDeeplink(wallet)}
                        className="w-full flex items-center gap-3 p-3 rounded-lg bg-white/5 hover:bg-white/10 border border-white/5 hover:border-white/10 transition-colors active:scale-[0.98]"
                    >
                        <img
                            src={wallet.icon}
                            alt={wallet.name}
                            className="size-9 rounded-xl"
                        />
                        <span className="flex-1 text-left text-sm font-bold tracking-tight">
                            {wallet.name}
                        </span>
                        <Icon name="chevron-right" size={18} className="opacity-30" />
                    </button>
                ))}
            </div>

            {/* Detected browser wallets (e.g. Brave Wallet) */}
            {browserWallets.length > 0 && (
                <>
                    <p className="text-white/40 text-xs mt-5 mb-3 leading-relaxed">
                        Browser wallets
                    </p>
                    <div className="space-y-2">
                        {browserWallets.map((wallet) => (
                            <button
                                key={wallet.adapter.name}
                                onClick={() => handleBrowserWallet(wallet)}
                                className="w-full flex items-center gap-3 p-3 rounded-lg bg-white/5 hover:bg-white/10 border border-white/5 hover:border-white/10 transition-colors active:scale-[0.98]"
                            >
                                <img
                                    src={wallet.adapter.icon}
                                    alt={wallet.adapter.name}
                                    className="size-9 rounded-xl"
                                />
                                <span className="flex-1 text-left text-sm font-bold tracking-tight">
                                    {wallet.adapter.name}
                                </span>
                                <Icon name="chevron-right" size={18} className="opacity-30" />
                            </button>
                        ))}
                    </div>
                </>
            )}

            <p className="text-white/20 text-[10px] mt-5 text-center leading-relaxed">
                Don&apos;t have a wallet? Tapping an option above will take you
                to the app store.
            </p>
        </BottomSheet>,
        document.body
    );
}
